import React from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';
import * as Icons from 'lucide-react';

const Services = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center bg-gradient-to-r from-blue-600 to-slate-700">
        <div className="container mx-auto px-4 z-10">
          <Badge className="mb-4 bg-white text-blue-600 border-0">Our Services</Badge>
          <h1 className="text-5xl font-bold text-white mb-6">
            Comprehensive Technical Solutions
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            From medical infrastructure to electrical systems, we deliver end-to-end solutions with precision and excellence.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = Icons[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={service.id}
                  className={`grid md:grid-cols-2 gap-12 items-center ${!isEven ? 'md:flex-row-reverse' : ''}`}
                >
                  <div className={isEven ? 'md:order-1' : 'md:order-2'}>
                    <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">
                      Service {service.id.toString().padStart(2, '0')}
                    </Badge>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-xl bg-blue-600 text-white flex items-center justify-center">
                        <IconComponent size={32} />
                      </div>
                      <h2 className="text-3xl font-bold text-slate-900">{service.title}</h2>
                    </div>
                    <p className="text-slate-600 mb-6 text-lg leading-relaxed">
                      {service.description}
                    </p>
                    <div className="space-y-3 mb-6">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <CheckCircle size={18} className="text-blue-600" />
                          </div>
                          <span className="text-slate-700 font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Link to="/contact">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                        Request Service <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Link>
                  </div>
                  <div className={isEven ? 'md:order-2' : 'md:order-1'}>
                    <Card className="overflow-hidden border-0 shadow-2xl">
                      <div className="relative h-80">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
                      </div>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">More Services</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Additional Capabilities</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Beyond our core services, we offer specialized solutions to meet diverse project requirements.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-slate-200 hover:shadow-xl transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center mb-4">
                  <Icons.Package size={24} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Goods Supply</h3>
                <p className="text-slate-600 mb-4">Supply of electronics, electrical equipment, plumbing materials, and furniture for complete project fulfillment.</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-blue-600 flex-shrink-0" />
                    Electronic Equipment
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-blue-600 flex-shrink-0" />
                    Electrical Materials
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-blue-600 flex-shrink-0" />
                    Plumbing Solutions
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-slate-200 hover:shadow-xl transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center mb-4">
                  <Icons.Wrench size={24} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Maintenance Services</h3>
                <p className="text-slate-600 mb-4">Comprehensive maintenance and after-sales support to ensure consistent performance of installed systems.</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-blue-600 flex-shrink-0" />
                    Preventive Maintenance
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-blue-600 flex-shrink-0" />
                    Emergency Repairs
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-blue-600 flex-shrink-0" />
                    24x7 Support
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border-slate-200 hover:shadow-xl transition-all">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center mb-4">
                  <Icons.Users size={24} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Manpower Supply</h3>
                <p className="text-slate-600 mb-4">Trained professionals for major modality service operations and equipment installation requirements.</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-blue-600 flex-shrink-0" />
                    Diploma & ITI Professionals
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-blue-600 flex-shrink-0" />
                    Trained Technicians
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-blue-600 flex-shrink-0" />
                    Installation Experts
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Our Services Stand Out</h2>
              <p className="text-slate-600">
                We combine technical expertise with customer-centric approach to deliver exceptional results.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Icons.Award className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Quality Assurance</h3>
                  <p className="text-slate-600 text-sm">Rigorous quality checks at every stage to ensure superior outcomes and long-lasting results.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Icons.Clock className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Timely Delivery</h3>
                  <p className="text-slate-600 text-sm">Committed to project timelines with efficient execution and proactive project management.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Icons.Shield className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Safety Standards</h3>
                  <p className="text-slate-600 text-sm">Adherence to industry safety standards and best practices in all our installations and operations.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Icons.HeadphonesIcon className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">Dedicated Support</h3>
                  <p className="text-slate-600 text-sm">Round-the-clock customer support with 3-hour response time in Delhi & NCR region.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-slate-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Need a Custom Solution?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Every project is unique. Let's discuss your specific requirements and create a tailored solution.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100 h-12 px-8">
              Discuss Your Project <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
